import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ReactDOMServer from 'react-dom/server'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Table, Tooltip, Modal, Form, Select, message } from 'antd'
import { CopyOutlined, LeftOutlined, SendOutlined, ReloadOutlined } from '@ant-design/icons'
import RoleDetailsToTPOTemplate from 'components/EmailTemplate/RoleDetailsToTPOTemplate'
import Button from 'components/Button'
import RichEditor from 'components/RichEditor'
import POCDetailsDrawer from 'components/UIComponents/POCDetailsDrawer'
import SuccessMessage from 'utils/SuccessMessage'
import LeftArrow from 'components/icons/LeftArrow'
import { StyledTableSqare } from 'components/TableStyles/TableStylesSqare/TableStylesSqare.js'
import TableSearchFilter from 'components/UIComponents/TableSearchFilter'
import Pagination from 'components/Pagination'
import ApprovedModelIcon from 'components/icons/ApprovedModelIcon'
import Drawer from 'components/Drawer'
import CopyFileIcon from 'components/icons/CopyFileIcon'
import SendIcon from 'components/icons/SendEmailIcon'
import Input from 'components/Form/Input'
import DropDown from 'components/Form/DropDown'
import ColoredTooltip from 'components/UIComponents/ColoredTooltip'
import PocAvailIcon from 'components/Images/Poc_Avail.svg'
import PocNotAvailIcon from 'components/Images/Chat_Circle_Close.svg'
import {
  getRoleLinks,
  getRoleLinkFilters,
  getRoleData,
  checkGmailAuth,
  startGmailAuth,
  sendRoleEmail,
  createCorporateInstituteDetails,
  updateCorporateInstituteDetails,
  regenerateTallyForm,
} from './actions'
import {
  PageWrapper,
  BreadcrumbText,
  BreadcrumbSeparator,
  PageHeader,
  PageTitle,
  BackArrow,
  TableWrapper,
  CollegeName,
  CollegeLogo,
  CollegeText,
  LocationText,
  TierBadge,
  LinkCell,
  CopyIcon,
  UpdatePocButton,
  ConfirmContent,
  ConfirmText,
  EmailFormWrapper,
  EmailLabel,
  SelectAllBanner,
  SelectAllLink,
  FilterBar,
  FilterField,
  FilterLabel,
  FilterSelect,
  FilterSegmented,
  ClearAllButton,
} from './style'
import BlueShareIcon from '../../components/icons/BlueShareIcon'

const RoleLinks = () => {
  const navigate = useNavigate()
  const { jobId } = useParams()
  const userData = useSelector(state => state.auth.userData)
  const corporateId = useSelector(state => state.auth.corporateId)

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [pageNo, setPageNo] = useState(0)
  const [pageLimit] = useState(10)
  const [searchValue, setSearchValue] = useState('')

  // Filters
  const DEFAULT_FILTERS = {
    state: undefined,
    city: undefined,
    tier: undefined,
    hasPoc: 'all',
    emailSent: 'all',
    formStatus: 'all',
  }
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [filterOptions, setFilterOptions] = useState({
    states: [],
    cities: [],
    tiers: [],
  })

  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  // Records selected across pages (when "Select All" is used). Keyed by jrimId
  // so per-college email payload survives pagination.
  const [selectedRecordsMap, setSelectedRecordsMap] = useState({})
  const [selectAllLoading, setSelectAllLoading] = useState(false)
  // Total eligible across all pages, learned after a select-all fetch.
  const [eligibleTotal, setEligibleTotal] = useState(null)
  const [roleData, setRoleData] = useState(null)
  const [generalFormUrl, setGeneralFormUrl] = useState(null)

  // POC Drawer
  const [pocDrawerOpen, setPocDrawerOpen] = useState(false)
  const [pocList, setPocList] = useState([])
  const [editingRecord, setEditingRecord] = useState({})
  const [pocEditingIndex, setPocEditingIndex] = useState(null)

  // Regenerate Tally Form
  const [regeneratingId, setRegeneratingId] = useState(null)

  // Send Email
  const [confirmVisible, setConfirmVisible] = useState(false)
  const [emailDrawerVisible, setEmailDrawerVisible] = useState(false)
  const [emailLoading, setEmailLoading] = useState(false)
  const [emailBody, setEmailBody] = useState('')
  const [editorKey, setEditorKey] = useState(0)
  const [form] = Form.useForm()

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const result = await getRoleLinks(jobId, {
        search: searchValue,
        state: filters.state,
        city: filters.city,
        tier: filters.tier,
        hasPoc: filters.hasPoc !== 'all' ? filters.hasPoc : undefined,
        emailSent: filters.emailSent !== 'all' ? filters.emailSent : undefined,
        formStatus:
          filters.formStatus !== 'all' ? filters.formStatus : undefined,
        pageNo,
        pageLimit,
      })
      if (result) {
        setData(result.result || [])
        setTotal(result.total || 0)
        setGeneralFormUrl(result.generalFormUrl || null)
      }
    } finally {
      setLoading(false)
    }
  }, [jobId, searchValue, filters, pageNo, pageLimit])

  const fetchFilterOptions = useCallback(async () => {
    const result = await getRoleLinkFilters(jobId)
    if (result) {
      setFilterOptions({
        states: result.states || [],
        cities: result.cities || [],
        tiers: result.tiers || [],
      })
    }
  }, [jobId])

  const fetchRoleData = useCallback(async () => {
    const result = await getRoleData(jobId)
    if (result) {
      setRoleData(result)
    }
  }, [jobId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    fetchFilterOptions()
  }, [fetchFilterOptions])

  // Reset cross-page selection when the search or any filter changes so a stale
  // "select all" doesn't include records that are no longer in scope.
  useEffect(() => {
    setSelectedRowKeys([])
    setSelectedRecordsMap({})
    setEligibleTotal(null)
  }, [searchValue, filters])

  useEffect(() => {
    fetchRoleData()
  }, [fetchRoleData])

  const handlePageChange = page => {
    setPageNo(page - 1)
  }

  // Any filter change resets to the first page so results aren't shown on a
  // page that no longer exists for the narrowed result set.
  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const next = { ...prev, [key]: value }
      // Clearing/Changing state invalidates a city that belonged to the old state.
      if (key === 'state') next.city = undefined
      return next
    })
    setPageNo(0)
  }

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS)
    setPageNo(0)
  }

  // City options are scoped to the selected state (if any).
  const cityOptions = useMemo(() => {
    const list = filters.state
      ? filterOptions.cities.filter(c => c.state === filters.state)
      : filterOptions.cities
    const seen = new Set()
    return list.filter(c => {
      if (!c.city || seen.has(c.city)) return false
      seen.add(c.city)
      return true
    })
  }, [filterOptions.cities, filters.state])

  const hasActiveFilters =
    !!filters.state ||
    !!filters.city ||
    filters.tier !== undefined ||
    filters.hasPoc !== 'all' ||
    filters.emailSent !== 'all' ||
    filters.formStatus !== 'all'

  const handleCopyFormUrl = () => {
    if (generalFormUrl) {
      navigator.clipboard.writeText(generalFormUrl)
      SuccessMessage('Link copied to clipboard')
    }
  }

  const handleCopyLink = (link) => {
    if (link) {
      navigator.clipboard.writeText(link)
      message.success('Link copied to clipboard')
    }
  }

  const handleRegenerateForm = async (record) => {
    const campusId = record?.instituteCampusId
    if (!campusId) return
    setRegeneratingId(campusId)
    try {
      const result = await regenerateTallyForm(jobId, campusId)
      if (result?.tallyFormUrl) {
        message.success('Tally form regenerated')
        fetchData()
      }
    } finally {
      setRegeneratingId(null)
    }
  }

  // POC Drawer handlers
  const handleEditPOC = (record) => {
    setEditingRecord(record)
    if (record?.pocDetails?.length > 0) {
      setPocList(record.pocDetails)
    } else {
      setPocList([])
    }
    setPocEditingIndex(null)
    setPocDrawerOpen(true)
  }

  const handleSavePOC = ({ poc, index, action }) => {
    setPocList(prev => {
      let newList = [...prev]
      if (action === 'add') {
        if (poc.isPrimary)
          newList = newList.map(p => ({ ...p, isPrimary: false }))
        newList.push(poc)
      } else if (action === 'update' && index !== null) {
        if (poc.isPrimary)
          newList = newList.map((p, i) =>
            i === index ? p : { ...p, isPrimary: false }
          )
        newList[index] = poc
      } else if (action === 'delete' && index !== null) {
        newList.splice(index, 1)
      }
      return newList
    })
    setPocEditingIndex(null)
  }

  const handleCancelPOC = () => {
    setPocEditingIndex(null)
    setPocDrawerOpen(false)
  }

  const handleUpdateInstituteDetails = async (
    selectedCollege,
    tier,
    pocDetails
  ) => {
    const campusId =
      selectedCollege?.instituteCampusId || selectedCollege?.id
    const payload = { instituteCampusId: campusId }
    if (tier !== undefined && tier !== null && tier !== 0) {
      payload.tier = tier
    }
    payload.pocDetails = pocDetails
    if (payload?.pocDetails === true) {
      delete payload.pocDetails
    }

    if (selectedCollege?.corporateDetailsId) {
      await updateCorporateInstituteDetails(
        corporateId,
        campusId,
        payload
      )
    } else {
      await createCorporateInstituteDetails(corporateId, [payload])
    }

    SuccessMessage('POC details updated successfully')
    setPocDrawerOpen(false)
    fetchData()
  }

  const handleSendEmailClick = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select at least one college')
      return
    }
    setConfirmVisible(true)
  }

  const handleConfirmYes = () => {
    setConfirmVisible(false)
    openEmailDrawer()
  }

  const openEmailDrawer = (record, preCollectedEmails = null) => {
    const companyName = roleData?.corporate?.name || roleData?.company?.companyName || 'Company'
    const roleName = roleData?.tittle || 'Role'
    const email = userData?.email || ''

    const toEmails = preCollectedEmails || []
    if (toEmails.length === 0) {
      if (record) {
        // Single record: get POC emails from that record
        const pocList = record?.pocDetails || []
        pocList.forEach(poc => {
          if (poc?.email) toEmails.push(poc.email)
        })
      } else {
        // Bulk send: get POC emails from all selected colleges (across pages)
        const selectedColleges = Object.values(selectedRecordsMap)
        selectedColleges.forEach(college => {
          const pocList = college?.pocDetails || []
          pocList.forEach(poc => {
            if (poc?.email && !toEmails.includes(poc.email)) {
              toEmails.push(poc.email)
            }
          })
        })
      }
    }

    const subject = `Job Opportunity for Students - Application Form ${companyName}`

    const userName = (userData?.first_name || '') + (userData?.last_name ? ' ' + userData?.last_name : '')
    const userPhone = userData?.phone_number || ''

    const body = ReactDOMServer.renderToString(
      <RoleDetailsToTPOTemplate
        data={{
          companyName,
          collegeName: '{{COLLEGE_NAME}}',
          tallyLink: '{{APPLICATION_FORM_LINK}}',
          signatureName: userName,
          signatureContact: userPhone,
        }}
      />
    )

    form.setFieldsValue({
      from: email,
      to: toEmails,
      subject,
      message: body,
    })
    setEmailBody(body)
    setEditorKey(prev => prev + 1)
    setEmailDrawerVisible(true)
  }

  const handleSendEmail = async () => {
    try {
      const values = await form.validateFields()
      const email = userData?.email

      // Check Gmail auth
      const authCheck = await checkGmailAuth(email)
      if (!authCheck?.authorized) {
        try {
          await startGmailAuth(email)
        } catch (err) {
          console.error('Gmail auth failed:', err)
          return
        }
      }

      setEmailLoading(true)

      const selectedColleges = Object.values(selectedRecordsMap)
      const instituteCampusIds = selectedColleges.map(
        item => item.instituteCampusId
      )

      // Build per-college data for personalized emails
      const colleges = selectedColleges.map(college => ({
        instituteCampusId: college.instituteCampusId,
        campusName: college.campusName || '',
        tallyFormUrl: college.tallyFormUrl || '',
        pocEmails: (college.pocDetails || [])
          .map(poc => poc?.email)
          .filter(Boolean),
      }))

      const payload = {
        from: email,
        to: values.to,
        cc: values.cc || [],
        subject: values.subject,
        body: values.message,
        roleId: jobId,
        instituteCampusIds,
        colleges,
      }

      const result = await sendRoleEmail(payload)

      if (result) {
        message.success('Email sent successfully')
        setEmailDrawerVisible(false)
        setSelectedRowKeys([])
        setSelectedRecordsMap({})
        form.resetFields()
        fetchData()
      }
    } catch (error) {
      console.error('Send email error:', error)
    } finally {
      setEmailLoading(false)
    }
  }

  const hasPoc = (record) => {
    return record?.pocDetails && Array.isArray(record.pocDetails) && record.pocDetails.length > 0
  }

  const isEligible = (record) => hasPoc(record) && !!record?.tallyFormUrl

  // Track selections across pages: union current page's selection with already-
  // selected rows on other pages. When a row is unchecked, drop it.
  const onSelectChange = (newSelectedRowKeys, newSelectedRows) => {
    setSelectedRecordsMap(prev => {
      const next = { ...prev }
      // Drop entries that exist on the current page but are no longer selected.
      data.forEach(row => {
        if (!newSelectedRowKeys.includes(row.jrimId)) {
          delete next[row.jrimId]
        }
      })
      // Add newly-selected rows from the current page.
      ;(newSelectedRows || []).forEach(row => {
        if (row?.jrimId) next[row.jrimId] = row
      })
      return next
    })
    setSelectedRowKeys(newSelectedRowKeys)
  }

  const handleSelectAllEligible = async () => {
    setSelectAllLoading(true)
    try {
      const result = await getRoleLinks(jobId, {
        search: searchValue,
        state: filters.state,
        city: filters.city,
        tier: filters.tier,
        hasPoc: filters.hasPoc !== 'all' ? filters.hasPoc : undefined,
        emailSent: filters.emailSent !== 'all' ? filters.emailSent : undefined,
        formStatus:
          filters.formStatus !== 'all' ? filters.formStatus : undefined,
        pageNo: 0,
        pageLimit: Math.max(total || 0, 1000),
      })
      const rows = result?.result || []
      const eligible = rows.filter(isEligible)
      setEligibleTotal(eligible.length)
      if (eligible.length === 0) {
        message.warning('No eligible colleges to select')
        return
      }
      const map = {}
      eligible.forEach(row => { map[row.jrimId] = row })
      setSelectedRecordsMap(map)
      setSelectedRowKeys(eligible.map(row => row.jrimId))
    } finally {
      setSelectAllLoading(false)
    }
  }

  const handleClearSelection = () => {
    setSelectedRecordsMap({})
    setSelectedRowKeys([])
  }

  const eligibleOnCurrentPage = data.filter(isEligible)
  const allCurrentPageSelected =
    eligibleOnCurrentPage.length > 0 &&
    eligibleOnCurrentPage.every(row => selectedRowKeys.includes(row.jrimId))

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: record => ({
      disabled: !hasPoc(record) || !record.tallyFormUrl,
    }),
  }

  const columns = [
    {
      title: 'College Name',
      dataIndex: 'campusName',
      key: 'campusName',
      width: 250,
      render: (text, record) => (
        <CollegeName>
          <CollegeLogo>
            {record.campusLogoUrl ? (
              <img src={record.campusLogoUrl} alt="" />
            ) : (
              <span style={{ fontSize: 14, color: '#98A2B3' }}>🏛</span>
            )}
          </CollegeLogo>
          <CollegeText>{text || 'N/A'}</CollegeText>
        </CollegeName>
      ),
    },
    {
      title: 'Location',
      dataIndex: 'city',
      key: 'location',
      width: 150,
      render: (text, record) => (
        <LocationText>
          {text || 'N/A'}
          {record.state ? `, ${record.state}` : ''}
        </LocationText>
      ),
    },
    {
      title: 'Tier',
      dataIndex: 'tier',
      key: 'tier',
      width: 80,
      align: 'center',
      render: tier => (
        tier ? <TierBadge tier={tier}>Tier {tier}</TierBadge> : '-'
      ),
    },
    {
      title: 'Link',
      dataIndex: 'tallyFormUrl',
      key: 'link',
      width: 280,
      render: (text, record) => {
        const pocPresent = hasPoc(record)
        const pocIcon = pocPresent ? (
          <ColoredTooltip title="POC" coloredTitle="Avail." color="#58B981">
            <img
              src={PocAvailIcon}
              alt="POC Available"
              style={{ width: 29, height: 29, cursor: 'pointer' }}
              onClick={() => handleEditPOC(record)}
            />
          </ColoredTooltip>
        ) : (
          <ColoredTooltip title="POC" coloredTitle="NA" color="#E35E50">
            <img
              src={PocNotAvailIcon}
              alt="POC Not Available"
              style={{ width: 35, height: 35, cursor: 'pointer' }}
              onClick={() => handleEditPOC(record)}
            />
          </ColoredTooltip>
        )

        // 1. No POC -> only show POC icon (click to add)
        if (!pocPresent) {
          return <LinkCell>{pocIcon}</LinkCell>
        }

        // 2. POC exists but no tally link -> POC icon + Regenerate
        if (!text) {
          const isLoading = regeneratingId === record.instituteCampusId
          return (
            <LinkCell>
              {pocIcon}
              <span style={{ color: '#667085', fontSize: 13 }}>No link generated</span>
              <Button.Secondary
                height={'32px'}
                text={isLoading ? 'Regenerating...' : 'Regenerate Form'}
                icon={<ReloadOutlined spin={isLoading} />}
                onClick={() => handleRegenerateForm(record)}
                disabled={isLoading}
              />
            </LinkCell>
          )
        }

        // 3. POC + link + email already sent -> POC icon + tallyFormUrl
        if (record.isEmailSent) {
          return (
            <LinkCell>
              {pocIcon}
              {record.tallyFormUrl}
              <Tooltip title="Copy link">
                <span onClick={() => handleCopyLink(text)} style={{ cursor: 'pointer' }}>
                  <CopyFileIcon />
                </span>
              </Tooltip>
            </LinkCell>
          )
        }

        // 4. POC + link + email NOT sent -> POC icon + "Send Email Now."
        return (
          <LinkCell>
            {pocIcon}
            <span><b>Send Email Now.</b></span>
            <span style={{ cursor: 'pointer' }}>
              <SendIcon />
            </span>
            <Tooltip title="Copy Link">
              <span onClick={() => handleCopyLink(record?.tallyFormUrl)} style={{ cursor: 'pointer' }}>
                <CopyFileIcon />
              </span>
            </Tooltip>
          </LinkCell>
        )
      },
    },
  ]

  return (
    <PageWrapper>
      {/* Breadcrumb */}
      <div style={{ marginBottom: 8 }}>
        <BreadcrumbText style={{ color: '#3249D7' }} onClick={() => navigate('/rolePage')}>
          Job Roles
        </BreadcrumbText>
        <BreadcrumbSeparator style={{ padding: '2px' }}>/</BreadcrumbSeparator>
        <BreadcrumbText>{roleData?.tittle || 'Role'}</BreadcrumbText>
      </div>

      {/* Page Header */}
      <PageHeader>
        <PageTitle>
          <span onClick={() => navigate('/rolePage')} style={{ cursor: 'pointer' }}>
            <LeftArrow />
          </span>
          Role Links
        </PageTitle>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Button.Secondary
            text="Copy Link"
            icon={<BlueShareIcon />}
            onClick={handleCopyFormUrl}
            disabled={!generalFormUrl}
          />
          <Button.Primary
            text="Send Emails"
            icon={<SendOutlined />}
            onClick={handleSendEmailClick}
            disabled={selectedRowKeys.length === 0}
          />
        </div>
      </PageHeader>

      {/* Search Row */}
      <TableSearchFilter
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        totalCount={total}
        currentCount={data.length > 0 ? Math.min((pageNo + 1) * pageLimit, total) : 0}
        placeholder="Search by college name"
        CandidateMenu={true}
      />

      {/* Filter bar */}
      <FilterBar>
        <FilterField width="170px">
          <FilterLabel>State</FilterLabel>
          <FilterSelect
            placeholder="All states"
            value={filters.state}
            onChange={value => handleFilterChange('state', value)}
            allowClear
            showSearch
            optionFilterProp="label"
            options={filterOptions.states.map(s => ({ label: s, value: s }))}
          />
        </FilterField>

        <FilterField width="170px">
          <FilterLabel>City</FilterLabel>
          <FilterSelect
            placeholder="All cities"
            value={filters.city}
            onChange={value => handleFilterChange('city', value)}
            allowClear
            showSearch
            optionFilterProp="label"
            options={cityOptions.map(c => ({ label: c.city, value: c.city }))}
          />
        </FilterField>

        <FilterField width="120px">
          <FilterLabel>Tier</FilterLabel>
          <FilterSelect
            placeholder="All tiers"
            value={filters.tier}
            onChange={value => handleFilterChange('tier', value)}
            allowClear
            options={filterOptions.tiers.map(t => ({
              label: `Tier ${t}`,
              value: t,
            }))}
          />
        </FilterField>

        <FilterField width="auto">
          <FilterLabel>POC</FilterLabel>
          <FilterSegmented
            value={filters.hasPoc}
            onChange={value => handleFilterChange('hasPoc', value)}
            options={[
              { label: 'All', value: 'all' },
              { label: 'With POC', value: 'with' },
              { label: 'Without', value: 'without' },
            ]}
          />
        </FilterField>

        <FilterField width="auto">
          <FilterLabel>Email</FilterLabel>
          <FilterSegmented
            value={filters.emailSent}
            onChange={value => handleFilterChange('emailSent', value)}
            options={[
              { label: 'All', value: 'all' },
              { label: 'Sent', value: 'sent' },
              { label: 'Not Sent', value: 'not_sent' },
            ]}
          />
        </FilterField>

        <FilterField width="auto">
          <FilterLabel>Form</FilterLabel>
          <FilterSegmented
            value={filters.formStatus}
            onChange={value => handleFilterChange('formStatus', value)}
            options={[
              { label: 'All', value: 'all' },
              { label: 'Generated', value: 'generated' },
              { label: 'Needs Form', value: 'needs_form' },
            ]}
          />
        </FilterField>

        <ClearAllButton
          onClick={handleClearFilters}
          disabled={!hasActiveFilters}
        >
          Clear all
        </ClearAllButton>
      </FilterBar>

      {/* Cross-page selection banner */}
      {(total > pageLimit || selectedRowKeys.length > 0) && (
        <SelectAllBanner>
          {selectedRowKeys.length > 0 ? (
            <>
              <span>
                <strong>{selectedRowKeys.length}</strong>
                {' '}college{selectedRowKeys.length === 1 ? '' : 's'} selected
                {selectedRowKeys.length > eligibleOnCurrentPage.length &&
                  ' across all pages'}
              </span>
              {total > pageLimit &&
                allCurrentPageSelected &&
                (eligibleTotal === null || selectedRowKeys.length < eligibleTotal) && (
                <SelectAllLink
                  onClick={handleSelectAllEligible}
                  disabled={selectAllLoading}
                >
                  {selectAllLoading
                    ? 'Selecting…'
                    : `Select all eligible colleges across ${Math.ceil(total / pageLimit)} pages`}
                </SelectAllLink>
              )}
              <SelectAllLink onClick={handleClearSelection}>
                Clear selection
              </SelectAllLink>
            </>
          ) : (
            <span>Select rows below, then use “Send Emails”.</span>
          )}
        </SelectAllBanner>
      )}

      {/* Table */}
      <StyledTableSqare
        rowSelection={rowSelection}
        columns={columns}
        dataSource={data}
        rowKey="jrimId"
        loading={loading}
        pagination={false}
        scroll={{ x: 800 }}
      />

      {/* Pagination */}
      {total > pageLimit && (
        <Pagination
          onChange={handlePageChange}
          totalPages={total}
          pageSize={pageLimit}
          current={pageNo + 1}
        />
      )}

      {/* POC Details Drawer */}
      {pocDrawerOpen && (
        <POCDetailsDrawer
          open={pocDrawerOpen}
          onClose={handleCancelPOC}
          initialPOC={{
            name: '',
            number: '',
            email: '',
            isPrimary: pocList.length === 0,
          }}
          onSave={handleSavePOC}
          pocList={pocList}
          editingRecord={editingRecord}
          pocEditingIndex={pocEditingIndex}
          setPocEditingIndex={setPocEditingIndex}
          handleUpdateInstituteDetails={handleUpdateInstituteDetails}
        />
      )}

      {/* Confirmation Modal */}
      <Modal
        visible={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        footer={null}
        centered
        width={428}
        closable={false}
      >
        <ConfirmContent>
          <ApprovedModelIcon />
          <ConfirmText>
            Are you sure you want to <strong>send the role</strong> to these{' '}
            <strong>{selectedRowKeys.length} colleges</strong>?
          </ConfirmText>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button.Secondary
              text="No"
              onClick={() => setConfirmVisible(false)}
              style={{ width: 80 }}
            />
            <Button.Primary
              text="Yes"
              onClick={handleConfirmYes}
              style={{ width: 80 }}
            />
          </div>
        </ConfirmContent>
      </Modal>

      {/* Send Email Drawer */}
      <Drawer
        title="Send Email"
        placement="right"
        onClose={() => {
          setEmailDrawerVisible(false)
          form.resetFields()
        }}
        closable
        visible={emailDrawerVisible}
        width={650}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button.Primary
              text={emailLoading ? 'Sending...' : 'Send Email'}
              onClick={handleSendEmail}
              loading={emailLoading}
              disabled={emailLoading}
            />
          </div>
        }
        content={
          <EmailFormWrapper>
            <Form form={form} layout="vertical">
              <EmailLabel>Email From *</EmailLabel>
              <Form.Item name="from">
                <Input disabled />
              </Form.Item>

              <EmailLabel>To *</EmailLabel>
              <Form.Item
                name="to"
                rules={[
                  { required: true, message: 'Please enter recipient email' },
                ]}
              >
                <DropDown
                  mode="tags"
                  placeholder="Enter recipient emails"
                  tokenSeparators={[',', ' ']}
                  open={false}
                  disabled
                />
              </Form.Item>

              <EmailLabel>Cc</EmailLabel>
              <Form.Item name="cc">
                <DropDown
                  mode="tags"
                  placeholder="Enter CC emails"
                  tokenSeparators={[',', ' ']}
                  open={false}
                />
              </Form.Item>

              <EmailLabel>Subject *</EmailLabel>
              <Form.Item
                name="subject"
                rules={[{ required: true, message: 'Please enter subject' }]}
              >
                <Input placeholder="Enter subject" />
              </Form.Item>

              <Form.Item
                name="message"
                rules={[{ required: true, message: 'Please enter message' }]}
              >
                <RichEditor
                  key={`editor-${editorKey}`}
                  labelName="Email Body"
                  value={emailBody}
                  onChange={value => {
                    setEmailBody(value)
                    form.setFieldsValue({ message: value })
                  }}
                  placeholder="Enter email body"
                />
              </Form.Item>
            </Form>
          </EmailFormWrapper>
        }
      />
    </PageWrapper>
  )
}

export default RoleLinks
